import { X } from 'lucide-react';
import type { CatalogFacets } from '@sbg/shared/schemas';
import { clearFilters, toggleFilterValue, toggleThickness } from '@/lib/catalog';
import type { CatalogFilterState } from '@/lib/catalog';
import { interpolate, useFormatters, useT } from '@/lib/i18n';
import type { ShopCopy } from './content';
import { categoryOptions, chipKey, refOptions } from './shop-utils';
import type { FilterChipId } from './shop-utils';
import type { UpdateMode } from './useShopFilters';

export interface ActiveFiltersProps {
  facets: CatalogFacets | undefined;
  filters: CatalogFilterState;
  onChange: (next: CatalogFilterState, mode?: UpdateMode) => void;
  copy: ShopCopy;
}

interface Chip {
  id: FilterChipId;
  label: string;
}

/**
 * What is filtered right now, as removable chips plus "Clear all". Labels come from the facet data
 * ; a value the facets do not know any more falls back to its slug.
 */
export function ActiveFilters({ facets, filters, onChange, copy }: ActiveFiltersProps) {
  const t = useT();
  const { formatMoney } = useFormatters();

  const chips: Chip[] = [];
  if (filters.category) {
    const label = categoryOptions(facets, filters).find((o) => o.selected)?.label;
    chips.push({ id: { kind: 'category' }, label: label ?? filters.category });
  }
  for (const key of ['color', 'finish', 'application', 'origin'] as const) {
    for (const option of refOptions(facets, filters, key)) {
      if (option.selected)
        chips.push({ id: { kind: 'list', key, value: option.value }, label: option.label });
    }
  }
  for (const mm of filters.thickness ?? []) {
    chips.push({ id: { kind: 'thickness', value: mm }, label: t('product.thickness', { mm }) });
  }
  for (const value of filters.availability ?? []) {
    chips.push({
      id: { kind: 'list', key: 'availability', value },
      label: t(`availability.${value}`),
    });
  }
  if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
    const low = filters.priceMin !== undefined ? formatMoney(filters.priceMin * 100) : '₹0';
    const high = filters.priceMax !== undefined ? formatMoney(filters.priceMax * 100) : '∞';
    chips.push({
      id: { kind: 'price' },
      label: interpolate(copy.active.price, { min: low, max: high }),
    });
  }
  if (filters.featured) chips.push({ id: { kind: 'featured' }, label: copy.active.featured });

  if (chips.length === 0) return null;

  const remove = (id: FilterChipId): void => {
    switch (id.kind) {
      case 'category': {
        const { category: _removed, ...rest } = filters;
        onChange(rest);
        return;
      }
      case 'list':
        onChange(toggleFilterValue(filters, id.key, id.value));
        return;
      case 'thickness':
        onChange(toggleThickness(filters, id.value));
        return;
      case 'price': {
        const { priceMin: _min, priceMax: _max, ...rest } = filters;
        onChange(rest);
        return;
      }
      case 'featured': {
        const { featured: _removed, ...rest } = filters;
        onChange(rest);
      }
    }
  };

  return (
    <div className="active-filters" role="group" aria-label={copy.active.label}>
      <ul className="active-filters__list">
        {chips.map((chip) => (
          <li key={chipKey(chip.id)}>
            <button
              type="button"
              className="active-chip"
              aria-label={interpolate(copy.active.remove, { label: chip.label })}
              onClick={() => {
                remove(chip.id);
              }}
            >
              <span>{chip.label}</span>
              <X size={14} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="active-filters__clear"
        onClick={() => {
          onChange(clearFilters(filters));
        }}
      >
        {copy.filters.clearAll}
      </button>
    </div>
  );
}
